import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Minus } from "lucide-react";

import { cn } from "@/utils";

interface SidebarToggleProps {
  collapsed: boolean;
  onToggle: () => void;
  /** Уведомляет родителя о hover state — для анимированного «дыхания» границы sidebar. */
  onHoverChange?: (hovering: boolean) => void;
}

// Базовые ширины sidebar — должны соответствовать классам в Sidebar.tsx
const SIDEBAR_FULL = 256; // w-64
const SIDEBAR_FULL_BREATH = 244; // w-[244px]
const SIDEBAR_RAIL = 64; // w-16
const SIDEBAR_RAIL_BREATH = 80; // w-20
const GAP = 4; // отступ от границы до кнопки

/**
 * Stripe-style collapse handle.
 *
 * Idle: тонкая вертикальная полоска ровно по правой границе sidebar — почти не видна.
 * Hover: полоска заменяется chevron'ом, который сдвигается на пару пикселей наружу,
 *        справа появляется dark-pill tooltip «Свернуть» / «Развернуть».
 */
export const SidebarToggle: React.FC<SidebarToggleProps> = ({ collapsed, onToggle, onHoverChange }) => {
  const [hover, setHover] = useState(false);

  const setHoverWithNotify = (v: boolean): void => {
    setHover(v);
    onHoverChange?.(v);
  };

  // Позиция кнопки следует за границей sidebar (включая breathing).
  // Hit-area сама не движется относительно границы, но "плавает" вместе с ней.
  const sidebarWidth = collapsed
    ? hover
      ? SIDEBAR_RAIL_BREATH
      : SIDEBAR_RAIL
    : hover
      ? SIDEBAR_FULL_BREATH
      : SIDEBAR_FULL;
  const baseLeft = sidebarWidth + GAP;

  return (
    <div
      className="hidden lg:block fixed z-40 top-1/2 -translate-y-1/2"
      style={{ left: `${baseLeft}px`, transition: "left 220ms cubic-bezier(0.4, 0, 0.2, 1)" }}
    >
      <button
        type="button"
        onClick={onToggle}
        onMouseEnter={() => setHoverWithNotify(true)}
        onMouseLeave={() => setHoverWithNotify(false)}
        aria-label={collapsed ? "Развернуть боковое меню" : "Свернуть боковое меню"}
        className={cn(
          "relative grid place-items-center",
          // Большая прозрачная hit-area (32×40), иконка центрирована внутри
          "w-8 h-10 -mx-2 -my-1.5",
          "transition-colors duration-150",
          hover ? "text-ink-1" : "text-ink-3",
        )}
      >
        {/* Idle: вертикальный Minus, hover: chevron в сторону «свернуть/развернуть». */}
        <span className="relative grid place-items-center w-3.5 h-3.5">
          <Minus
            size={14}
            strokeWidth={2.5}
            className={cn(
              "absolute inset-0 m-auto rotate-90 transition-opacity duration-150",
              hover ? "opacity-0" : "opacity-100",
            )}
          />
          {collapsed ? (
            <ChevronRight
              size={14}
              strokeWidth={2.5}
              className={cn(
                "absolute inset-0 m-auto transition-opacity duration-150",
                hover ? "opacity-100" : "opacity-0",
              )}
            />
          ) : (
            <ChevronLeft
              size={14}
              strokeWidth={2.5}
              className={cn(
                "absolute inset-0 m-auto transition-opacity duration-150",
                hover ? "opacity-100" : "opacity-0",
              )}
            />
          )}
        </span>

        {/* Tooltip — dark pill справа от иконки (фон фикс. тёмный, не зависит от темы) */}
        {hover && (
          <span
            className={cn(
              "absolute left-full ml-3 top-1/2 -translate-y-1/2 whitespace-nowrap",
              "px-2.5 py-1 rounded-md bg-zinc-900 text-fs-xs font-medium text-white",
              "shadow-shell-md pointer-events-none",
            )}
          >
            {collapsed ? "Развернуть" : "Свернуть"}
          </span>
        )}
      </button>
    </div>
  );
};

export default SidebarToggle;
